/**
 * `<SlotCardDragRank>` — ordered list with up/down buttons for the
 * `drag-rank` slot type.
 *
 * No `@dnd-kit/core` in the renderer (yet), so we use the simpler
 * keyboard-friendly up/down approach. The output is `string[]`
 * carrying the option ids in priority order; the parent submits this
 * directly via `interview:answer-slot`.
 *
 * Pre-fill: when the engine supplied a prefilled rank, the parent
 * passes it as the initial `value`. Touching any reorder button
 * clears the `prefilledUntouched` styling.
 */

import type { SlotDefinition } from '@volley/crucible/interview/types.js';

export interface SlotCardDragRankProps {
  slot: SlotDefinition;
  /** Ordered list of option ids — most-preferred first. */
  value: string[];
  onChange: (next: string[]) => void;
  prefilledUntouched: boolean;
}

/** Pure helper — move the item at `index` up or down by one slot.
 *  No-op at the boundaries (can't move first up; can't move last
 *  down). Exported for tests. */
export function reorder(
  list: readonly string[],
  index: number,
  direction: 'up' | 'down',
): string[] {
  const next = [...list];
  if (direction === 'up' && index > 0) {
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
  } else if (direction === 'down' && index < next.length - 1) {
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
  }
  return next;
}

/** Seed the initial rank from a slot's options when no prefill exists.
 *  Preserves the SKILL's option order — assumed already in the
 *  designer's preferred default. Exported so the parent `<SlotCard>`
 *  can call it without mounting the component. */
export function defaultRankFromOptions(slot: SlotDefinition): string[] {
  return [...(slot.options ?? [])];
}

export function SlotCardDragRank({
  slot,
  value,
  onChange,
  prefilledUntouched,
}: SlotCardDragRankProps): React.JSX.Element {
  const move = (index: number, direction: 'up' | 'down'): void => {
    onChange(reorder(value, index, direction));
  };

  return (
    <div data-testid={`slot-card-drag-rank-${slot.id}`}>
      <p className="mb-2 text-[10px] uppercase tracking-wide text-white/40">
        Order from most to least important
      </p>
      <ol className="space-y-1.5">
        {value.map((opt, idx) => (
          <li
            key={opt}
            data-testid={`slot-card-drag-rank-${slot.id}-row`}
            data-option={opt}
            data-rank={idx + 1}
            className={`flex items-center gap-2 rounded-md border border-white/10 bg-black/20 px-3 py-2 ${
              prefilledUntouched ? 'opacity-60' : ''
            }`}
          >
            <span className="w-6 text-center text-xs font-semibold text-white/40">
              {idx + 1}
            </span>
            <span className="flex-1 text-sm text-white/80">{opt}</span>
            <button
              type="button"
              onClick={() => move(idx, 'up')}
              disabled={idx === 0}
              data-testid={`slot-card-drag-rank-${slot.id}-up`}
              data-row-index={idx}
              aria-label={`Move ${opt} up`}
              className="rounded border border-white/10 bg-white/5 px-2 py-0.5 text-xs text-white/60 transition-colors hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => move(idx, 'down')}
              disabled={idx === value.length - 1}
              data-testid={`slot-card-drag-rank-${slot.id}-down`}
              data-row-index={idx}
              aria-label={`Move ${opt} down`}
              className="rounded border border-white/10 bg-white/5 px-2 py-0.5 text-xs text-white/60 transition-colors hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
            >
              ↓
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
